import { NgClass, NgOptimizedImage, NgStyle } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextService } from '@services/context';
import { ResponsiveService } from '@shared/services/responsive';
import { getUuid } from '@shared/utils';
import { debounceTime, merge, skip, Subject, take } from 'rxjs';
import { Image, ImageSize, MAX_PIXEL_DENSITY, MAX_PREGENERATION_SIZE, Size } from './models';
import { getCDNLink } from './utils';

@Component({
  selector: 'app-image',
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, NgOptimizedImage, NgClass],
})
export class ImageComponent implements OnInit, OnDestroy {
  /**
   * The main image input (required).
   * Contains basic image information such as title, link, alt, etc.
   */
  readonly image = input.required<Image>();

  /**
   * Defines the configured size (width/height) for both mobile and desktop.
   * If not provided, the component will try to fill the available space.
   */
  readonly size = input<ImageSize>();

  /**
   * If true, preserves the aspect ratio when resizing the image.
   * When false, the component can adjust dimensions to fill available space.
   */
  readonly keepAspectRatio = input(false);

  /**
   * Enables specific loading optimizations if true (e.g., fetchpriority="high").
   * Useful for the Largest Contentful Paint (LCP) image.
   */
  readonly isPriority = input(false);

  /** Represents the loading state (true = loading, false = fully loaded). */
  readonly isLoading = model(true);

  /** A reference to the DOM element wrapping the image. */
  readonly wrapperImageElement = viewChild<ElementRef<HTMLElement>>('wrapperImage');

  /** Reactive signal for the computed width of the component container. */
  readonly width = signal<number>(0);

  /** Reactive signal for the computed height of the component container. */
  readonly height = signal<number>(0);
  /**
   * Indicates whether the image should use a "fill" mode,
   * typically applying object-cover if no explicit dimensions are set.
   */
  readonly isFullWidth = signal(false);
  /**
   * Holds the array of images, usually a single-entry array
   * with a computed CDN link that takes into account the current dimensions.
   */
  readonly imagesArray = signal<Image[] | null>(null);
  /** Represents the error state (true = error, false = no error). */
  readonly isError = signal(false);
  /** Determines whether to display a loading placeholder while the image is being loaded. */
  readonly showLoadingPlaceholder = signal(true);
  /** Service to handle responsive layout changes and breakpoints */
  readonly #responsiveService = inject(ResponsiveService);
  /** Current breakpoint (e.g., 'mobile', 'desktop') derived from ResponsiveService. */
  readonly breakpoints = this.#responsiveService.breakpoints;
  /** A boolean variable indicating whether the current environment is a server.*/
  readonly #isServer = inject(ContextService).isServer();
  /**
   * Device's pixel ratio used for image quality adjustments. The server has no screen to measure
   * and every dense display would stretch what it writes: measured on the homepage, twelve of
   * sixteen photos doubled on a 2x display.
   */
  readonly #devicePixelRatio = this.#isServer ? MAX_PIXEL_DENSITY : this.#responsiveService.devicePixelRatio;
  /**
   * Internal ResizeObserver to watch for container size changes
   * and update width/height signals accordingly.
   */
  #resizeObserver: ResizeObserver | null = null;
  /** Subject used to emit resize events so they can be processed. */
  readonly #resizeSubject = new Subject<{ width: number; height: number }>();

  constructor() {
    // Schedule the ResizeObserver initialization after the next render cycle.
    afterNextRender(() => {
      this.#initResizeObserver();
    });

    /**
     * Observe the first resize event immediately (take(1)),
     * then observe subsequent events with a 200ms debounce to avoid frequent updates.
     */
    const firstEvent$ = this.#resizeSubject.pipe(take(1));
    const subsequentEvents$ = this.#resizeSubject.pipe(skip(1), debounceTime(200));

    merge(firstEvent$, subsequentEvents$)
      .pipe(takeUntilDestroyed())
      .subscribe(({ width, height }) => {
        // Update internal dimensions
        this.#updateDimensions(width, height);

        // Generate a new CDN link based on updated dimensions
        const cdnLink = getCDNLink(
          this.image(),
          {
            width: this.width(),
            height: this.height(),
          },
          this.#devicePixelRatio
        );

        // Check whether we need to update the image array with the new link
        if (this.#shouldUpdateImage(cdnLink)) {
          this.isLoading.set(true);

          // Refresh the image entry with the newly built CDN link
          const newImage = {
            ...this.image(),
            cdnLink,
            randomId: getUuid(),
          };
          this.imagesArray.set([newImage]);
        }
      });

    effect(() => {
      const currentImage = this.image();
      const currentWidth = this.width();
      const currentHeight = this.height();

      if (!currentImage || currentWidth <= 0 || currentHeight <= 0) {
        return;
      }

      const cdnLink = getCDNLink(
        currentImage,
        {
          width: currentWidth,
          height: currentHeight,
        },
        this.#devicePixelRatio
      );

      if (this.#shouldUpdateImage(cdnLink)) {
        this.isLoading.set(true);
        this.isError.set(false);

        const newImage = {
          ...currentImage,
          cdnLink,
          randomId: getUuid(),
        };
        this.imagesArray.set([newImage]);
      }
    });
  }

  /**
   * Angular lifecycle hook that is called after component initialization.
   * Calculates and sets the width and height properties based on the image sizes.
   * Generates a new CDN link for the image with the updated dimensions and updates the `imagesArray`.
   */
  ngOnInit(): void {
    if (!this.isLoading()) {
      this.showLoadingPlaceholder.set(false);
    }
    // This part is used for SSG (Static Site Generation) to avoid CLS (Cumulative Layout Shift).
    this.#handleServerImageRendering();
  }

  /**
   * Lifecycle hook that performs cleanup.
   * Disconnects the ResizeObserver and completes the Subject.
   */
  ngOnDestroy(): void {
    // Stop observing size changes
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#resizeSubject.complete();
  }

  /**
   * Manually set the loading state to false upon image load completion.
   * This can be used in the template's load or error event handlers.
   */
  setLoadingFalse(): void {
    this.isLoading.set(false);
  }

  onError(): void {
    this.isError.set(true);
    this.isLoading.set(false);
  }

  /**
   * Handles the rendering of an image on the server side by adjusting its dimensions,
   * updating its CDN link, and generating a new random identifier.
   */
  #handleServerImageRendering(): void {
    // Check if we are in a server environment (e.g., Angular Universal)
    if (this.#isServer) {
      const { width, height } = this.#getSizes(this.size()?.mw ?? -1, this.size()?.mh ?? -1);

      // If the computed width is -1, enable full-width mode
      if (width === -1) {
        this.#setFullWidthMode();
      }

      // The layout keeps the dimensions it always had: the pregeneration size is only ever
      // the size asked of the CDN, and feeding it back here would resize the box.
      this.width.set(width);
      this.height.set(height);

      const cdnLink = getCDNLink(this.image(), this.#getPregenerationSize(width, height), this.#devicePixelRatio);

      const newImage = {
        ...this.image(),
        cdnLink,
        randomId: getUuid(),
      };

      this.imagesArray.set([newImage]);
    }
  }

  /**
   * Size asked of the CDN by the server pass, which runs before any container is measured.
   *
   * A block configuring nothing for mobile leaves both dimensions at -1, the URL then carries
   * no resize parameter at all and the CDN returns the source file. Aiming at the file's own
   * definition instead, bounded, is what stops that.
   */
  #getPregenerationSize(width: number, height: number): Size {
    if (width > 0 || height > 0) {
      return this.#boundToMaxSize({ width, height });
    }

    const intrinsic = this.image().gravity;

    // Nothing configured and no dimensions on the media either. Bounding the width alone lets
    // the CDN keep the file's ratio, which is all it takes to stop it serving the source.
    if (!intrinsic?.width || !intrinsic.height) {
      return { width: MAX_PREGENERATION_SIZE / MAX_PIXEL_DENSITY, height: 0 };
    }

    return this.#boundToMaxSize({ width: intrinsic.width, height: intrinsic.height });
  }

  /**
   * Scales a size down until both sides fit `MAX_PREGENERATION_SIZE` once the density above has
   * multiplied them. A side left at -1 stays negative, so it keeps emitting no parameter.
   */
  #boundToMaxSize({ width, height }: Size): Size {
    const max = MAX_PREGENERATION_SIZE / MAX_PIXEL_DENSITY;
    const scale = Math.min(width > 0 ? max / width : 1, height > 0 ? max / height : 1, 1);

    return { width: Math.floor(width * scale), height: Math.floor(height * scale) };
  }

  /**
   * Sets up a ResizeObserver to detect size changes of the wrapper element,
   * then emits new dimensions through #resizeSubject.
   */
  #initResizeObserver(): void {
    const elm = this.wrapperImageElement()?.nativeElement;
    if (!elm) return;
    this.#resizeObserver = new ResizeObserver(() => {
      // Emit current offset dimensions
      this.#resizeSubject.next({
        width: elm.offsetWidth,
        height: elm.offsetHeight,
      });
    });

    // Start observing the wrapper element
    this.#resizeObserver.observe(elm);
  }

  /**
   * Updates width/height signals based on container size,
   * current breakpoints (mobile/desktop), and the aspect ratio setting.
   */
  #updateDimensions(containerWidth: number, containerHeight: number): void {
    // Retrieve final dimensions according to configuration and container size
    const { width, height } = this.#getSizes(containerWidth, containerHeight);
    this.width.set(width);
    this.height.set(height);

    this.#setFullWidthMode();
  }

  /**
   * Determine if the component should apply "fill" mode
   * by checking the provided sizeConfig and the keepAspectRatio setting.
   */
  #setFullWidthMode(): void {
    const sizeConfig = this.size();
    const isMobile = this.breakpoints() === 'mobile';
    this.isFullWidth.set(
      isMobile ? sizeConfig?.mw == null && !this.keepAspectRatio() : sizeConfig?.dw == null && !this.keepAspectRatio()
    );
  }

  /**
   * Checks whether the image should be updated based on valid dimensions
   * and a link that differs from the existing one.
   */
  #shouldUpdateImage(newCdnLink: string): boolean {
    const images = this.imagesArray();
    const firstImageCDNLink = images?.[0]?.cdnLink ?? null;
    const hasValidSize = this.width() > 0 && this.height() > 0;
    const isDifferentLink = newCdnLink !== firstImageCDNLink;

    // Update only if dimensions are valid and the new link is different
    return hasValidSize && isDifferentLink;
  }

  /**
   * Calculates the width/height based on container dimensions,
   * the current breakpoint (mobile/desktop), and the aspect ratio setting.
   */
  #getSizes(containerWidth: number, containerHeight: number): Size {
    const isMobileBreakpoint = this.breakpoints() === 'mobile';
    const shouldMaintainAspectRatio = this.keepAspectRatio();
    const configuredSize = this.size() ?? {};

    // If aspect ratio is preserved, compute dimensions accordingly
    if (shouldMaintainAspectRatio) {
      return this.#getWidthAspectRatio(containerWidth);
    }

    // Otherwise, use explicit mobile/desktop dimensions or fallback to container sizes
    const width = isMobileBreakpoint ? (configuredSize.mw ?? containerWidth) : (configuredSize.dw ?? containerWidth);
    const height = isMobileBreakpoint ? (configuredSize.mh ?? containerHeight) : (configuredSize.dh ?? containerHeight);

    return { width, height };
  }

  /**
   * Respects the original image aspect ratio, applies optional max-size constraints,
   * and returns computed width/height based on the container's width.
   */
  #getWidthAspectRatio(containerWidth: number): Size {
    const isMobile = this.breakpoints() === 'mobile';
    const sizeConfig = this.size() ?? {};

    if (!sizeConfig.mw && sizeConfig.mh) {
      delete sizeConfig.mh;
    }
    if (!sizeConfig.dw && sizeConfig.dh) {
      delete sizeConfig.dh;
    }

    const { width: imageWidth, height: imageHeight } = this.image().gravity;
    const aspectRatio = imageWidth / imageHeight;

    const configuredWidth = isMobile ? sizeConfig.mw : sizeConfig.dw;
    const configuredHeight = isMobile ? sizeConfig.mh : sizeConfig.dh;

    const finalWidth = configuredWidth ?? containerWidth;
    const tempHeight = finalWidth / aspectRatio;
    const appliedHeight = configuredHeight != null ? Math.min(tempHeight, configuredHeight) : tempHeight;

    // By default, default to container width and let the height be dynamically computed
    // or further constrained if needed by sizeConfig.
    return {
      width: finalWidth,
      height: Math.round(appliedHeight),
    };
  }
}
